import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { Copy } from 'iconsax-react';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { diApi } from './api';
import type { DataSourceSummary, PipelineSummary, SourceTable } from './types';
import StateChip from './components/StateChip';
import NewPipelineDialog from './components/NewPipelineDialog';
import SftpDocumentsCard from './components/SftpDocumentsCard';
import PipelineRunControl from './components/PipelineRunControl';
import FieldCatalogueDrawer from './FieldCatalogueDrawer';
import { entityLabel, errorText, formatCount, formatDayRange, relativeTime, sourceTables, summariseCounters } from './components/format';

interface Props {
  clientUid: string;
  /** Bumped by the parent when something changed elsewhere (e.g. the editor published). */
  refreshKey?: number;
  onOpenPipeline: (pipelineUid: string) => void;
  /** Switch to the Documents tab. */
  onOpenDocuments: () => void;
}

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };

const sourceStatus = (s: DataSourceSummary): { label: string; color: 'success' | 'warning' | 'default' } => {
  if (!s.enabled) return { label: 'Disabled', color: 'default' };
  if (s.pipelinesStopped) return { label: 'Receiving · pipelines stopped', color: 'warning' };
  return { label: 'Receiving', color: 'success' };
};

const versionMeta = (p: PipelineSummary): string => {
  if (!p.liveVersion) return `Draft v${p.draftVersion} · not published yet`;
  if (p.draftVersion > p.liveVersion) return `v${p.liveVersion} running · editing draft v${p.draftVersion}`;
  return `v${p.liveVersion} running`;
};

const HEALTH: Record<PipelineSummary['health'], { label: string; color: string }> = {
  HEALTHY: { label: 'Healthy', color: 'success.main' },
  WARNING: { label: 'Needs a look', color: 'warning.main' },
  FAILING: { label: 'Failing', color: 'error.main' },
  NOT_TESTED: { label: 'Not tested', color: 'grey.400' }
};

function HealthDot({ health, message }: { health: PipelineSummary['health']; message?: string }) {
  const h = HEALTH[health] ?? HEALTH.NOT_TESTED;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: h.color, flexShrink: 0 }} />
      <Typography variant="body2" color={health === 'NOT_TESTED' ? 'text.secondary' : 'text.primary'}>
        {message || h.label}
      </Typography>
    </Stack>
  );
}

function BlockHead({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {hint}
        </Typography>
      </Box>
      {action}
    </Stack>
  );
}

/** What hovering a table's count explains: its real name, when it last arrived, how its records are keyed. */
function tableHint(t: SourceTable): string {
  const key = t.keyedBy.includes('content')
    ? 'no key field, so each change is stored as its own record'
    : `keyed by ${t.keyedBy.join(', ')}`;
  const parts = [`Table "${t.name}"`, t.lastRecordAt ? `last record ${relativeTime(t.lastRecordAt)}` : null, key];
  if (!t.documented) parts.push("not in Aljex's table list");
  return parts.filter(Boolean).join(' · ');
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ minHeight: 30 }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{children}</Box>
    </Stack>
  );
}

export default function Overview({ clientUid, refreshKey = 0, onOpenPipeline, onOpenDocuments }: Props) {
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const [sources, setSources] = useState<DataSourceSummary[] | null>(null);
  const [pipelines, setPipelines] = useState<PipelineSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [catalogueSource, setCatalogueSource] = useState<DataSourceSummary | null>(null);
  const [confirm, setConfirm] = useState<{ source: DataSourceSummary; action: 'stop' | 'resume' } | null>(null);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const requestId = useRef(0);

  const load = useCallback(
    (silent = false) => {
      const ticket = ++requestId.current;
      if (!silent) setLoading(true);
      setError(null);
      Promise.all([diApi.getSources(clientUid), diApi.getPipelines(clientUid)])
        .then(([s, p]) => {
          if (ticket !== requestId.current) return;
          if (s.success === false) setError(s.message || 'Could not load sources.');
          else setSources(s.data ?? []);
          if (p.success === false) setError(p.message || 'Could not load pipelines.');
          else setPipelines(p.data ?? []);
        })
        .catch((err) => {
          if (ticket === requestId.current) setError(errorText(err, 'Could not load data integrations.'));
        })
        .finally(() => {
          if (ticket === requestId.current) setLoading(false);
        });
    },
    [clientUid]
  );

  useEffect(() => {
    setSources(null);
    setPipelines(null);
    load();
  }, [load]);

  useEffect(() => {
    if (refreshKey) load(true);
  }, [refreshKey, load]);

  const copyEndpoint = async (endpoint: string) => {
    try {
      await navigator.clipboard.writeText(endpoint);
      toastRef.current('Endpoint copied', 'success');
    } catch {
      toastRef.current('Could not copy. Select the endpoint and copy it by hand.', 'error');
    }
  };

  const runSourceAction = async () => {
    if (!confirm) return;
    const { source, action } = confirm;
    setBusy(true);
    try {
      const res = action === 'stop' ? await diApi.stopSource(clientUid, source.uid) : await diApi.resumeSource(clientUid, source.uid);
      if (res.success === false) {
        toastRef.current(res.message || 'Something went wrong', 'error');
        return;
      }
      if (res.data) setSources((prev) => (prev ?? []).map((s) => (s.uid === source.uid ? res.data : s)));
      toastRef.current(
        action === 'stop'
          ? `All pipelines on ${source.name} are stopped.`
          : 'Pipelines resumed. They work through the queued records first.',
        action === 'stop' ? 'warning' : 'success'
      );
      setConfirm(null);
      load(true);
    } catch (err) {
      toastRef.current(errorText(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !sources && !pipelines) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading data integrations" />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
        Data that this client's systems send to QuikStep, and the pipelines that turn it into transactions. Only Super Admins see this tab.
      </Typography>

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* ------------------------------ Sources ------------------------------ */}
      <Box>
        <BlockHead title="Sources" hint="Connections that send records to QuikStep" />
        <Grid container spacing={2}>
          {(sources ?? []).map((s) => {
            const status = sourceStatus(s);
            return (
              <Grid item xs={12} lg={7} key={s.uid}>
                <MainCard content={false} sx={{ p: 2.5, height: '100%' }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Box>
                        <Typography variant="h5">{s.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Descartes Aljex
                          {s.tenantQual ? (
                            <>
                              {' · tenant '}
                              <Box component="code" sx={mono}>
                                {s.tenantQual}
                              </Box>
                            </>
                          ) : null}
                          {' · sends each change as it happens'}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        color={status.color}
                        variant={status.color === 'default' ? 'outlined' : 'light'}
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'currentColor' }} />
                            <span>{status.label}</span>
                          </Stack>
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>

                    <Box>
                      <Stack direction="row" spacing={4} rowGap={1.5} flexWrap="wrap" useFlexGap>
                        {sourceTables(s).map((t) => (
                          <Tooltip key={t.name} title={tableHint(t)} placement="top" arrow>
                            <Box>
                              <Typography variant="caption" color={t.documented ? 'text.secondary' : 'warning.main'}>
                                {entityLabel(t.name)}
                                {!t.documented && ' · new'}
                              </Typography>
                              <Typography variant="h4">{formatCount(t.count)}</Typography>
                            </Box>
                          </Tooltip>
                        ))}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {s.firstRecordAt || s.lastRecordAt
                          ? `Records received ${formatDayRange(s.firstRecordAt, s.lastRecordAt)} · last record ${relativeTime(
                              s.lastRecordAt
                            )}`
                          : 'No records received yet'}
                      </Typography>
                    </Box>

                    <Stack spacing={0.5} sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5 }}>
                      <Fact label="Endpoint">
                        <Box component="code" sx={{ ...mono, wordBreak: 'break-all' }}>
                          {s.endpoint}
                        </Box>
                        <Button size="small" variant="text" startIcon={<Copy size={14} />} onClick={() => copyEndpoint(s.endpoint)}>
                          Copy
                        </Button>
                      </Fact>
                      <Fact label="Sign-in">
                        <Typography variant="body2">
                          HTTP Basic ·{' '}
                          <Box component="code" sx={mono}>
                            {s.basicAuthUser || '—'}
                          </Box>
                        </Typography>
                      </Fact>
                      <Fact label="Allowed IPs">
                        {s.allowedIps?.length ? (
                          s.allowedIps.map((ip) => <Chip key={ip} size="small" variant="outlined" label={ip} sx={mono} />)
                        ) : (
                          <>
                            <Chip size="small" color="warning" variant="light" label="Any" sx={{ fontWeight: 600 }} />
                            <Typography variant="caption" color="text.secondary">
                              Ask Descartes for their source IP
                            </Typography>
                          </>
                        )}
                      </Fact>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" color="secondary" onClick={() => setCatalogueSource(s)}>
                        Field catalogue
                      </Button>
                      {s.pipelinesStopped ? (
                        <Button variant="outlined" color="primary" onClick={() => setConfirm({ source: s, action: 'resume' })}>
                          Resume pipelines
                        </Button>
                      ) : (
                        <Button variant="outlined" color="error" onClick={() => setConfirm({ source: s, action: 'stop' })}>
                          Emergency stop
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </MainCard>
              </Grid>
            );
          })}

          {sources && sources.length === 0 && (
            <Grid item xs={12} lg={7}>
              <MainCard content={false} sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h5">No live source yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Aljex Live Sync appears here once it is set up for this client.
                </Typography>
              </MainCard>
            </Grid>
          )}

          <Grid item xs={12} lg={5}>
            <SftpDocumentsCard clientUid={clientUid} refreshKey={refreshKey} onOpen={onOpenDocuments} />
          </Grid>
        </Grid>
      </Box>

      {/* ------------------------------ Pipelines ------------------------------ */}
      <Box>
        <BlockHead
          title="Pipelines"
          hint="Each one decides which records become transactions, and how"
          action={
            <Button variant="contained" onClick={() => setNewOpen(true)}>
              New pipeline
            </Button>
          }
        />
        <MainCard content={false}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pipeline</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Sends to</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell align="right">Last 24 h</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {(pipelines ?? []).map((p) => {
                  const last24 = summariseCounters(p.last24h);
                  return (
                    <TableRow
                      key={p.uid}
                      hover
                      tabIndex={0}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onOpenPipeline(p.uid)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenPipeline(p.uid);
                        }
                      }}
                    >
                      <TableCell>
                        <Typography variant="subtitle1">{p.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {versionMeta(p)}
                        </Typography>
                      </TableCell>
                      <TableCell>{entityLabel(p.entity)}</TableCell>
                      <TableCell>{p.targetSummary || <Typography color="text.secondary">Not chosen yet</Typography>}</TableCell>
                      <TableCell>
                        <StateChip state={p.state} />
                      </TableCell>
                      <TableCell align="right">
                        {last24 ? (
                          <Tooltip
                            title={`Matched ${p.last24h.matched} · created ${p.last24h.created} · would create ${p.last24h.wouldCreate} · adopted ${p.last24h.adopted} · updated ${p.last24h.updated} · flagged ${p.last24h.flagged} · skipped ${p.last24h.skipped} · failed ${p.last24h.failed}`}
                          >
                            <Typography variant="body2">{last24}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <HealthDot health={p.health} message={p.healthMessage} />
                      </TableCell>
                      <TableCell align="right">
                        <PipelineRunControl
                          clientUid={clientUid}
                          pipeline={p}
                          size="small"
                          onChanged={(d) => setPipelines((prev) => (prev ?? []).map((x) => (x.uid === d.uid ? { ...x, ...d } : x)))}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {pipelines && pipelines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                        <Typography variant="subtitle1">No pipelines yet</Typography>
                        <Typography variant="body2" color="text.secondary">
                          A pipeline picks which records become transactions, where they go and how the first step is filled.
                        </Typography>
                        <Button variant="outlined" onClick={() => setNewOpen(true)} disabled={!sources?.length}>
                          New pipeline
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MainCard>
      </Box>

      <FieldCatalogueDrawer
        open={Boolean(catalogueSource)}
        onClose={() => setCatalogueSource(null)}
        clientUid={clientUid}
        source={catalogueSource}
      />

      <Dialog open={Boolean(confirm)} onClose={busy ? undefined : () => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.action === 'stop' ? 'Stop every pipeline on this source?' : 'Resume pipelines on this source?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.action === 'stop'
              ? 'Records are still received and stored, so nothing is lost. Pipelines catch up when you resume.'
              : 'Pipelines work through the records that arrived while they were stopped first, then carry on as each change arrives. Live pipelines create transactions for those records.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm(null)} disabled={busy} color="secondary">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.action === 'stop' ? 'error' : 'primary'}
            onClick={runSourceAction}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {confirm?.action === 'stop' ? 'Stop pipelines' : 'Resume pipelines'}
          </Button>
        </DialogActions>
      </Dialog>

      <NewPipelineDialog
        open={newOpen}
        clientUid={clientUid}
        sources={sources ?? undefined}
        onClose={() => setNewOpen(false)}
        onCreated={(uid) => {
          setNewOpen(false);
          load(true);
          onOpenPipeline(uid);
        }}
      />
    </Stack>
  );
}
